import { action, computed, makeObservable } from 'mobx';

import { InsightEntity } from '@/entities/Insights';
import { FieldCheckedStore } from '@/stores/local/shared/form/FieldCheckedStore';
import { FieldTextStore } from '@/stores/local/shared/form/FieldTextStore';
import { FormStore } from '@/stores/local/shared/form/FormStore';
import { OptionEntity } from '@/types/OptionEntity';

import { LifespanSelectStore } from '../LifespanSelectStore';
import { LIFESPAN_OPTIONS } from '../LifespanSelectStore/config';
import { TopicSelectStore } from '../TopicSelectStore';

type FieldsMap = {
  title: FieldTextStore;
  topic: TopicSelectStore;
  lifespan: LifespanSelectStore;
  text: FieldTextStore;
  isPublic: FieldCheckedStore;
};

type FormCreateNoteStoreParams = {
  organizationId: string;
};

type PrivateFields = '_title' | '_text';

export class FormCreateNoteStore extends FormStore<FieldsMap> {
  fieldsMap: FieldsMap;

  constructor({ organizationId }: FormCreateNoteStoreParams) {
    super();

    this.fieldsMap = {
      title: new FieldTextStore({ defaultValue: 'Новый инсайт', required: true }),
      topic: new TopicSelectStore({ organizationId, options: { required: true } }),
      lifespan: new LifespanSelectStore({ required: true }),
      text: new FieldTextStore({ defaultValue: '', sanitize: false, required: true }),
      isPublic: new FieldCheckedStore({ defaultValue: true }),
    };

    makeObservable<FormCreateNoteStore, PrivateFields>(this, {
      _title: computed,
      _text: computed,
      serverFormat: computed,
      setUpdateInsight: action.bound,
    });
  }

  private get _title(): string {
    return (this.fieldsMap.title.serverFormat ?? '').trim();
  }

  private get _text(): string {
    return (this.fieldsMap.text.serverFormat ?? '').trim();
  }

  get serverFormat() {
    return {
      title: this._title,
      text: this._text,
      topic_id: this.fieldsMap.topic.serverFormat,
      expiration_date: this.fieldsMap.lifespan.serverFormat,
      duration_months: this.fieldsMap.lifespan.value[0].durationMonths,
      is_public: this.fieldsMap.isPublic.serverFormat,
    };
  }

  submit() {}

  setUpdateInsight(insight: InsightEntity) {
    const title = insight.title ?? 'Новый инсайт';
    const topic: OptionEntity = {
      value: insight.topic.name,
      key: String(insight.topic.id),
    };
    const lifespan =
      LIFESPAN_OPTIONS.find((item) => item.durationMonths === insight.durationMonths) || LIFESPAN_OPTIONS[0];
    const markdownText = insight?.text ?? '';
    const isPublic = insight.isPublic;

    this.fieldsMap.title.setValue(title, { noTouch: true });
    this.fieldsMap.title.setDefaultValue(title);

    this.fieldsMap.topic.setValue([topic], { noTouch: true });
    this.fieldsMap.topic.setDefaultValue([topic]);

    this.fieldsMap.lifespan.setValue([lifespan], { noTouch: true });
    this.fieldsMap.lifespan.setDefaultValue([lifespan]);

    this.fieldsMap.text.setValue(markdownText, { noTouch: true });
    this.fieldsMap.text.setDefaultValue(markdownText);

    this.fieldsMap.isPublic.setValue(isPublic, { noTouch: true });
    this.fieldsMap.isPublic.setDefaultValue(isPublic);
  }
}
